import React, { useEffect, useState, useCallback } from "react";
import adminApi from "@/lib/adminApi";
import { Loader2, Eye, Euro, TrendingUp, ChevronDown, BarChart3, CalendarDays, Trophy } from "lucide-react";

const money = (n) => `€ ${Number(n || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
const monthLabel = (key) => { const [y, m] = String(key).split("-"); return `${MONTHS_IT[parseInt(m, 10) - 1] || m} ${y}`; };
const RANK_STYLES = ["bg-[#B2A475] text-[#0B0B0C]", "bg-[#C7C7CC] text-[#0B0B0C]", "bg-[#CD7F32] text-white"];
const PERIODS = [{ v: "total", l: "Totale" }, { v: "month", l: "Questo mese" }, { v: "days30", l: "Ultimi 30 giorni" }, { v: "year", l: "Quest'anno" }];
const VIEWS_LABEL = { total: "Visualizzazioni totali", month: "Visualizzazioni (questo mese)", days30: "Visualizzazioni (30 giorni)", year: "Visualizzazioni (quest'anno)" };

const Counter = ({ label, value, icon: Icon, cls = "text-white" }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-4" data-testid={`platform-counter-${label}`}>
    <div className="flex items-center gap-2 text-xs text-white/50"><Icon className="w-3.5 h-3.5" /> {label}</div>
    <p className={`text-2xl font-bold mt-1 ${cls}`}>{value}</p>
  </div>
);

const PlatformStatsSection = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(true);
  const [period, setPeriod] = useState("total");

  const load = useCallback(async () => {
    try {
      const r = await adminApi.get("/admin/platform-stats", { params: { period } });
      setData(r.data);
    } catch (e) { /* silenzioso: sezione superadmin */ }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="mt-6 bg-white/[0.03] border border-white/10 rounded-2xl overflow-hidden" data-testid="platform-stats-section">
      <button onClick={() => setOpen((o) => !o)} data-testid="platform-stats-toggle" className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left hover:bg-white/[0.03]">
        <span className="flex items-center gap-2 font-semibold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
          <BarChart3 className="w-5 h-5 text-[#B2A475]" /> <span className="text-lg gold-text">Statistiche piattaforma</span>
        </span>
        <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-4 pb-5 sm:px-5">
          {!data ? (
            <div className="h-24 flex items-center justify-center text-white/50 gap-2"><Loader2 className="w-5 h-5 animate-spin" /> Caricamento…</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
                <span className="text-xs text-white/50">Periodo visualizzazioni</span>
                <div className="flex gap-1.5 flex-wrap">
                  {PERIODS.map((p) => (
                    <button key={p.v} onClick={() => setPeriod(p.v)} data-testid={`platform-period-${p.v}`} className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${period === p.v ? "bg-[#B2A475] text-[#0B0B0C] border-[#B2A475]" : "border-white/15 text-white/70 hover:bg-white/5"}`}>{p.l}</button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Counter label={VIEWS_LABEL[period] || "Visualizzazioni"} value={data.total_views} icon={Eye} />
                <Counter label="Incasso mese corrente" value={money(data.current_month_income)} icon={TrendingUp} cls="text-emerald-400" />
                <Counter label="Incasso prossimi 12 mesi" value={money(data.next_12_income)} icon={Euro} cls="text-[#B2A475]" />
              </div>

              {(() => {
                const ranked = data.restaurants.filter((r) => r.total_views > 0).slice(0, 5);
                if (ranked.length === 0) return null;
                const top = ranked[0].total_views || 1;
                return (
                  <div className="mt-4 rounded-2xl border border-[#B2A475]/25 bg-[#B2A475]/[0.06] p-4" data-testid="platform-leaderboard">
                    <p className="flex items-center gap-2 text-sm font-semibold gold-text mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                      <Trophy className="w-4 h-4 text-[#B2A475]" /> <span className="text-base">Classifica locali più visti</span>
                    </p>
                    <div className="space-y-2">
                      {ranked.map((r, i) => (
                        <div key={r.id} className="flex items-center gap-3" data-testid={`platform-leader-${i + 1}`}>
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${RANK_STYLES[i] || "bg-white/10 text-white/70"}`}>{i + 1}</span>
                          <span className="text-sm font-medium truncate flex-1 min-w-0">{r.name}</span>
                          <div className="hidden sm:block flex-1 max-w-[45%]">
                            <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                              <div className="h-full rounded-full bg-[#B2A475]" style={{ width: `${Math.round((r.total_views / top) * 100)}%` }} />
                            </div>
                          </div>
                          <span className="inline-flex items-center gap-1 text-sm font-semibold text-white tabular-nums flex-shrink-0"><Eye className="w-3.5 h-3.5 text-[#B2A475]" /> {r.total_views}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {data.projection?.length > 0 && (() => {
                const maxAmt = Math.max(...data.projection.map((p) => p.amount));
                return (
                  <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid="platform-projection">
                    <p className="flex items-center gap-2 text-sm font-semibold text-white/80 mb-3"><CalendarDays className="w-4 h-4 text-[#B2A475]" /> Calendario incassi previsti</p>
                    <div className="space-y-2">
                      {data.projection.map((p) => (
                        <div key={p.month} className="flex items-center gap-3" data-testid={`projection-${p.month}`}>
                          <span className="text-xs text-white/60 w-16 sm:w-20 flex-shrink-0 capitalize">{monthLabel(p.month)}</span>
                          <div className="flex-1 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full rounded-full bg-emerald-500/70" style={{ width: `${Math.round((p.amount / maxAmt) * 100)}%` }} /></div>
                          <span className="text-sm font-semibold text-white tabular-nums w-20 sm:w-24 text-right flex-shrink-0">{money(p.amount)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default PlatformStatsSection;
